import { supabase } from './supabaseClient';
import { Questionnaire, QuestionnaireQuestion, CompletedQuestionnaire, QuestionnaireStatus } from '../types/questionnaire';
import { getAuthenticatedUser } from '../utils/sessionValidationUtil';

export interface QuestionOption {
  id?: string;
  option_text: string;
  position: number;
}

export interface Question {
  id?: string;
  question_text: string;
  question_type: 'single_choice' | 'multiple_choice';
  options: QuestionOption[];
  position: number;
}

export interface QuestionAnswer {
  question_id: string;
  selected_option_ids: string[];
}

export interface CreateQuestionnaireInput {
  title: string;
  description?: string;
  rewardPoints: number;
  maxParticipants?: number;
  skillRequired?: string;
  questions: Question[];
  publisherId: string;
  countryCode: string;
}

interface QuestionnaireResult {
  questionnaire: Questionnaire | null;
  questions: QuestionnaireQuestion[];
  error: any;
  notAvailable?: boolean;
  alreadyTaken?: boolean;
}

export interface QuestionnaireResponse {
  questionnaires?: Questionnaire[];
  error?: { message: string } | null;
}

export interface CompletedQuestionnaireResponse {
  questionnaires?: CompletedQuestionnaire[];
  error?: { message: string } | null;
}

export class QuestionnaireService {
  static async createQuestionnaire(input: CreateQuestionnaireInput) {
    try {
      console.log('Creating questionnaire with input:', input);

      // Start a transaction to create questionnaire and its questions/options
      const { data: questionnaire, error: questionnaireError } = await supabase
        .from('questionnaires')
        .insert([{
          title: input.title,
          description: input.description,
          reward_points: input.rewardPoints,
          max_participants: input.maxParticipants,
          skill_required: input.skillRequired,
          total_questions: input.questions.length,
          publisher_id: input.publisherId,
          country_code: input.countryCode,
          status: 'open', // Using 'open' status to match RLS policy
        }])
        .select()
        .single();

      if (questionnaireError) throw questionnaireError;
      if (!questionnaire) throw new Error('Failed to create questionnaire');

      console.log('Created questionnaire successfully:', questionnaire);

      // Insert questions
      for (const question of input.questions) {
        const { data: questionData, error: questionError } = await supabase
          .from('questionnaire_questions')
          .insert([{
            questionnaire_id: questionnaire.id,
            question_text: question.question_text,
            question_type: question.question_type,
            position: question.position,
          }])
          .select()
          .single();

        if (questionError) throw questionError;
        if (!questionData) throw new Error('Failed to create question');

        // Insert options for the question
        const { error: optionsError } = await supabase
          .from('questionnaire_options')
          .insert(
            question.options.map(option => ({
              question_id: questionData.id,
              option_text: option.option_text,
              position: option.position,
            }))
          );

        if (optionsError) throw optionsError;
      }

      return { data: questionnaire, error: null };
    } catch (error) {
      console.error('Create questionnaire error:', error);
      return { data: null, error };
    }
  }

  static async getAvailableQuestionnaires(userId: string, countryCode: string): Promise<QuestionnaireResponse> {
    try {
      // OPTIMIZATION: Run both queries in parallel instead of sequentially
      const [completedResult, questionnairesResult] = await Promise.all([
        // Get questionnaires the user has already completed
        supabase
          .from('questionnaire_submissions')
          .select('questionnaire_id')
          .eq('user_id', userId),
        // Get all available questionnaires (we'll filter completed ones client-side)
        supabase
          .from('questionnaires')
          .select(`
            *,
            questionnaire_questions(count),
            questionnaire_submissions(count)
          `)
          .in('status', ['published', 'open'])
          .eq('country_code', countryCode)
          .neq('publisher_id', userId)
      ]);

      if (questionnairesResult.error) {
        console.error('Error fetching questionnaires:', questionnairesResult.error);
        return { questionnaires: [], error: questionnairesResult.error };
      }

      // Create a Set for O(1) lookup instead of using .not('id', 'in', ...) which creates long URLs
      const completedIds = new Set(
        completedResult.data?.map(sub => sub.questionnaire_id) || []
      );

      // Filter out completed questionnaires client-side (avoids long URL issue)
      const filteredData = (questionnairesResult.data || []).filter(
        q => !completedIds.has(q.id)
      );

      // Process the data to add total_questions
      const processedData = filteredData.map(questionnaire => ({
        ...questionnaire,
        total_questions: Array.isArray(questionnaire.questionnaire_questions) && questionnaire.questionnaire_questions.length > 0
          ? questionnaire.questionnaire_questions[0].count || questionnaire.total_questions || 0
          : questionnaire.total_questions || 0
      }));

      return { questionnaires: processedData, error: null };
    } catch (error) {
      console.error('Exception in getAvailableQuestionnaires:', error);
      return { questionnaires: [], error: { message: 'Failed to fetch questionnaires' } };
    }
  }

  static async getMyPublishedQuestionnaires(): Promise<QuestionnaireResponse> {
    try {
      // Use getAuthenticatedUser() - fast cached access with expiration validation
      // Will trigger auth error if session expired (handled by global error handlers)
      const user = await getAuthenticatedUser();
      if (!user) {
        return { questionnaires: [], error: { message: 'User not authenticated' } };
      }

      const { data, error } = await supabase
        .from('questionnaires')
        .select(`
          *,
          questionnaire_questions(count),
          questionnaire_submissions(count)
        `)
        .eq('publisher_id', user.id)
        .order('created_at', { ascending: false });

      // Process the data to add total_questions from the count and actual response count
      const processedData = data?.map(questionnaire => ({
        ...questionnaire,
        total_questions: Array.isArray(questionnaire.questionnaire_questions) && questionnaire.questionnaire_questions.length > 0
          ? questionnaire.questionnaire_questions[0].count || questionnaire.total_questions || 0
          : questionnaire.total_questions || 0,
        current_participants: Array.isArray(questionnaire.questionnaire_submissions) && questionnaire.questionnaire_submissions.length > 0
          ? questionnaire.questionnaire_submissions[0].count || questionnaire.current_participants || 0
          : questionnaire.current_participants || 0
      })) || [];

      return { questionnaires: processedData, error };
    } catch (error) {
      return { questionnaires: [], error: { message: 'Failed to fetch questionnaires' } };
    }
  }

  static async getMyCompletedQuestionnaires(): Promise<CompletedQuestionnaireResponse> {
    try {
      // Use getAuthenticatedUser() - fast cached access with expiration validation
      const user = await getAuthenticatedUser();
      if (!user) {
        return { questionnaires: [], error: { message: 'User not authenticated' } };
      }

      const { data, error } = await supabase
        .from('questionnaire_submissions')
        .select(`
          id,
          submitted_at,
          awarded_points,
          questionnaires:questionnaire_id (
            id,
            publisher_id,
            title,
            description,
            total_questions,
            reward_points,
            max_participants,
            skill_required,
            country_code,
            status,
            current_participants,
            created_at,
            closes_at
          )
        `)
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match the CompletedQuestionnaire interface
      // Filter out submissions where questionnaire data is null (deleted questionnaires)
      const completedQuestionnaires = data?.filter((submission: any) => {
        return submission.questionnaires != null;
      }).map((submission: any) => ({
        ...submission.questionnaires,
        total_questions: submission.questionnaires?.total_questions || 0,
        submission_id: submission.id,
        submitted_at: submission.submitted_at,
        awarded_points: submission.awarded_points
      })) || [];

      return {
        questionnaires: completedQuestionnaires,
        error: null
      };
    } catch (error) {
      console.error('Exception in getMyCompletedQuestionnaires:', error);
      return { questionnaires: [], error: { message: 'Failed to fetch completed questionnaires' } };
    }
  }

  static async getQuestionnaireForTaking(questionnaireId: string, userId: string): Promise<QuestionnaireResult> {
    try {
      const { data: questionnaire, error: questionnaireError } = await supabase
        .from('questionnaires')
        .select('*')
        .eq('id', questionnaireId)
        .single();

      if (questionnaireError) throw questionnaireError;

      if (!questionnaire) {
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'Questionnaire not found' },
          notAvailable: true
        };
      }

      // Check if user is the publisher
      if (questionnaire.publisher_id === userId) {
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'Publishers cannot take their own surveys' },
          notAvailable: true
        };
      }

      // Check if user has already completed this questionnaire
      const { data: existingSubmission } = await supabase
        .from('questionnaire_submissions')
        .select('id')
        .eq('questionnaire_id', questionnaireId)
        .eq('user_id', userId)
        .maybeSingle();

      if (existingSubmission) {
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'You have already completed this survey' },
          alreadyTaken: true
        };
      }

      // Check if questionnaire is open for submissions
      if (questionnaire.status !== 'published' && questionnaire.status !== 'open') {
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'This survey is no longer available' },
          notAvailable: true
        };
      }

      // Fetch questions first
      const { data: questions, error: questionsError } = await supabase
        .from('questionnaire_questions')
        .select('*')
        .eq('questionnaire_id', questionnaireId)
        .order('position');

      if (questionsError) {
        console.error('Error fetching questions:', questionsError);
        throw questionsError;
      }

      console.log('Fetched questions for questionnaire:', questionnaireId, 'Questions count:', questions?.length || 0);

      // Check if questionnaire has no questions
      if (!questions || questions.length === 0) {
        console.warn('Questionnaire has no questions:', questionnaireId);
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'This questionnaire has no questions. Please contact the questionnaire creator.' },
          notAvailable: true
        };
      }

      // Fetch options for each question
      const questionsWithOptions = [];
      for (const question of questions) {
        const { data: options, error: optionsError } = await supabase
          .from('questionnaire_options')
          .select('*')
          .eq('question_id', question.id)
          .order('position');

        if (optionsError) {
          console.error('Error fetching options for question:', question.id, optionsError);
          throw optionsError;
        }

        questionsWithOptions.push({
          ...question,
          options: options || []
        });
      }

      // Validate that each question has options
      const questionsWithoutOptions = questionsWithOptions.filter(q => !q.options || q.options.length === 0);
      if (questionsWithoutOptions.length > 0) {
        console.warn('Some questions have no options:', questionsWithoutOptions.map(q => q.id));
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'Some questions in this questionnaire are incomplete. Please contact the questionnaire creator.' },
          notAvailable: true
        };
      }

      return {
        questionnaire,
        questions: questionsWithOptions || [],
        error: null
      };
    } catch (error) {
      return {
        questionnaire: null,
        questions: [],
        error
      };
    }
  }
  static async submitQuestionnaire(questionnaireId: string, answers: QuestionAnswer[]) {
    try {
      // Use getAuthenticatedUser() - fast cached access with expiration validation
      const user = await getAuthenticatedUser();
      if (!user) {
        return { success: false, error: { message: 'User not authenticated' } };
      }

      // Convert answers to the format expected by the database function
      const formattedAnswers = answers.map(answer => ({
        question_id: answer.question_id,
        selected_option_ids: answer.selected_option_ids
      }));

      console.log('Submitting questionnaire with formatted answers:', {
        questionnaireId,
        answersCount: formattedAnswers.length,
        formattedAnswers
      });

      // Call the database function that handles everything properly
      const { data, error } = await supabase.rpc('submit_questionnaire', {
        p_questionnaire_id: questionnaireId,
        p_answers: formattedAnswers
      });

      if (error) {
        console.error('Database function error:', error);
        return { success: false, error: { message: error.message } };
      }

      console.log('Questionnaire submission successful:', data);

      // Import the SurveyMessageIntegration dynamically to avoid circular dependencies
      const { SurveyMessageIntegration } = await import('../utils/surveyMessageIntegration');

      // After successful submission, check if survey is complete and notify publisher
      // This needs to be done asynchronously but we don't need to wait for it
      // Get the submission ID from the database
      const { data: submissionData } = await supabase
        .from('questionnaire_submissions')
        .select('id')
        .eq('questionnaire_id', questionnaireId)
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .single();

      if (submissionData) {
        // Fire and forget - don't block the response
        SurveyMessageIntegration.checkAndHandleSurveyCompletion({
          questionnaireId,
          submissionId: submissionData.id
        }).catch(err => {
          console.error('Error checking survey completion:', err);
          // Don't throw - message creation failure shouldn't affect submission success
        });
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Submit questionnaire error:', error);
      return { success: false, error: { message: 'Failed to submit questionnaire' } };
    }
  }

  static async getQuestionnaireDetails(questionnaireId: string) {
    try {
      const { data, error } = await supabase
        .from('questionnaires')
        .select('*')
        .eq('id', questionnaireId)
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  static async getQuestionnaireResults(questionnaireId: string) {
    try {
      const { data, error } = await supabase
        .from('questionnaire_results')
        .select('*')
        .eq('questionnaire_id', questionnaireId);

      return { data, error };
    } catch (error) {
      return { data: [], error };
    }
  }

  static async getSubmissionCount(questionnaireId: string) {
    try {
      const { count, error } = await supabase
        .from('questionnaire_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('questionnaire_id', questionnaireId);

      return { data: count, error };
    } catch (error) {
      return { data: 0, error };
    }
  }

  static async updateParticipantCount(questionnaireId: string) {
    try {
      // Get the current participant count
      const { count } = await supabase
        .from('questionnaire_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('questionnaire_id', questionnaireId);

      // Update the questionnaire with the current participant count
      const { error } = await supabase
        .from('questionnaires')
        .update({ current_participants: count || 0 })
        .eq('id', questionnaireId);

      if (error) throw error;

      return { success: true, count: count || 0 };
    } catch (error) {
      console.error('Error updating participant count:', error);
      return { success: false, error };
    }
  }

  static async updateQuestionnaire(questionnaireId: string, input: CreateQuestionnaireInput) {
    try {
      console.log('Updating questionnaire:', questionnaireId, 'with input:', input);

      // Update the questionnaire details
      const { data: questionnaire, error: questionnaireError } = await supabase
        .from('questionnaires')
        .update({
          title: input.title,
          description: input.description,
          reward_points: input.rewardPoints,
          max_participants: input.maxParticipants,
          skill_required: input.skillRequired,
          total_questions: input.questions.length,
          country_code: input.countryCode,
        })
        .eq('id', questionnaireId)
        .select()
        .single();

      if (questionnaireError) throw questionnaireError;
      if (!questionnaire) throw new Error('Failed to update questionnaire');

      console.log('Updated questionnaire successfully:', questionnaire);

      // Delete existing questions and their options
      // First get all question IDs for this questionnaire
      const { data: existingQuestions } = await supabase
        .from('questionnaire_questions')
        .select('id')
        .eq('questionnaire_id', questionnaireId);

      if (existingQuestions && existingQuestions.length > 0) {
        const questionIds = existingQuestions.map(q => q.id);

        // Delete options first (they reference questions)
        const { error: deleteOptionsError } = await supabase
          .from('questionnaire_options')
          .delete()
          .in('question_id', questionIds);

        if (deleteOptionsError) {
          console.warn('Could not delete existing options:', deleteOptionsError);
        }

        // Then delete the questions
        const { error: deleteQuestionsError } = await supabase
          .from('questionnaire_questions')
          .delete()
          .eq('questionnaire_id', questionnaireId);

        if (deleteQuestionsError) {
          console.warn('Could not delete existing questions:', deleteQuestionsError);
        }
      }

      // Insert new questions
      for (const question of input.questions) {
        const { data: questionData, error: questionError } = await supabase
          .from('questionnaire_questions')
          .insert([{
            questionnaire_id: questionnaireId,
            question_text: question.question_text,
            question_type: question.question_type,
            position: question.position,
          }])
          .select()
          .single();

        if (questionError) throw questionError;
        if (!questionData) throw new Error('Failed to create question');

        // Insert options for the question
        const { error: optionsError } = await supabase
          .from('questionnaire_options')
          .insert(
            question.options.map(option => ({
              question_id: questionData.id,
              option_text: option.option_text,
              position: option.position,
            }))
          );

        if (optionsError) throw optionsError;
      }

      return { data: questionnaire, error: null };
    } catch (error) {
      console.error('Update questionnaire error:', error);
      return { data: null, error };
    }
  }

  static async getQuestionnaireForEditing(questionnaireId: string, userId: string) {
    try {
      // Get questionnaire details first
      const { data: questionnaire, error: questionnaireError } = await supabase
        .from('questionnaires')
        .select('*')
        .eq('id', questionnaireId)
        .single();

      if (questionnaireError) throw questionnaireError;

      if (!questionnaire) {
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'Questionnaire not found' }
        };
      }

      // Check if user is the publisher (for editing, we only allow owners)
      if (questionnaire.publisher_id !== userId) {
        return {
          questionnaire: null,
          questions: [],
          error: { message: 'You do not have permission to edit this questionnaire' }
        };
      }

      // Fetch questions
      const { data: questions, error: questionsError } = await supabase
        .from('questionnaire_questions')
        .select('*')
        .eq('questionnaire_id', questionnaireId)
        .order('position');

      if (questionsError) {
        console.error('Error fetching questions for editing:', questionsError);
        throw questionsError;
      }

      console.log('Fetched questions for editing:', questionnaireId, 'Questions count:', questions?.length || 0);

      // Fetch options for each question
      const questionsWithOptions = [];
      if (questions && questions.length > 0) {
        for (const question of questions) {
          const { data: options, error: optionsError } = await supabase
            .from('questionnaire_options')
            .select('*')
            .eq('question_id', question.id)
            .order('position');

          if (optionsError) {
            console.error('Error fetching options for question:', question.id, optionsError);
            throw optionsError;
          }

          questionsWithOptions.push({
            ...question,
            options: options || []
          });
        }
      }

      return {
        questionnaire,
        questions: questionsWithOptions || [],
        error: null
      };
    } catch (error) {
      console.error('Error in getQuestionnaireForEditing:', error);
      return {
        questionnaire: null,
        questions: [],
        error
      };
    }
  }

  // Debug method to investigate questionnaire structure
  static async debugQuestionnaire(questionnaireId: string) {
    try {
      console.log('🔍 Debugging questionnaire:', questionnaireId);

      // Check questionnaire exists
      const { data: questionnaire, error: qError } = await supabase
        .from('questionnaires')
        .select('*')
        .eq('id', questionnaireId)
        .single();

      console.log('📋 Questionnaire data:', questionnaire);
      console.log('❌ Questionnaire error:', qError);

      if (!questionnaire) {
        console.log('❌ Questionnaire not found');
        return { questionnaire: null, questions: [], options: [] };
      }

      // Check questions
      const { data: questions, error: questionsError } = await supabase
        .from('questionnaire_questions')
        .select('*')
        .eq('questionnaire_id', questionnaireId)
        .order('position');

      console.log('❓ Questions data:', questions);
      console.log('❌ Questions error:', questionsError);
      console.log('📊 Questions count:', questions?.length || 0);

      // Check options for each question
      let allOptions: any[] = [];
      if (questions && questions.length > 0) {
        for (const question of questions) {
          const { data: options, error: optionsError } = await supabase
            .from('questionnaire_options')
            .select('*')
            .eq('question_id', question.id)
            .order('position');

          console.log(`🔘 Options for question ${question.id}:`, options);
          console.log(`❌ Options error for question ${question.id}:`, optionsError);

          if (options) {
            allOptions = [...allOptions, ...options];
          }
        }
      }

      console.log('🔘 Total options:', allOptions.length);

      return { questionnaire, questions: questions || [], options: allOptions };
    } catch (error) {
      console.error('🚨 Debug error:', error);
      return { questionnaire: null, questions: [], options: [], error };
    }
  }
}
